import { useState } from 'react'
import { PanelError } from '@/console/api/errors'
import { WarehousePolicy } from '@/console/api/types'
import { toMasterGateError } from '@/console/api/warehousePolicy'
import { saveWarehousePolicy } from '@/console/data/warehousePolicy'

/** 카드 · 헤더가 셸에 넘기는 콜백 묶음. */
export type WriteCallbacks = {
  /** 저장/전환 시작 true, 끝 false. 워크스페이스가 세어서 목록·탭바를 잠근다. */
  onSavingChange: (saving: boolean) => void
  /** 쓰기가 1건이라도 성공하면 부른다 — 목록(`missingAttributes`·`active`)을 다시 조회한다. */
  onSaved: () => void
}

/**
 * LOGISPRD-15602 — 카드 하나의 쓰기 상태(진행 중 · 마지막 오류).
 *
 * 저장 성공 시 data 계층이 창고를 재조회해 카드의 `key` 가 바뀌면 카드가 리마운트될 수 있다 —
 * 그래서 잠금 해제(`onSavingChange(false)`)는 컴포넌트 effect 가 아니라 `finally` 에서 직접 부른다.
 */
export const useWriteAction = ({
  onSavingChange,
}: Pick<WriteCallbacks, 'onSavingChange'>): {
  saving: boolean
  error: PanelError | null
  setError: (error: PanelError | null) => void
  run: <T>(task: () => Promise<T>, onDone?: (result: T) => void) => void
} => {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<PanelError | null>(null)

  const run = <T>(task: () => Promise<T>, onDone?: (result: T) => void): void => {
    setSaving(true)
    onSavingChange(true)
    setError(null)
    task()
      .then((result) => {
        onDone?.(result)
      })
      .catch((err) => setError(toMasterGateError(err)))
      .finally(() => {
        setSaving(false)
        onSavingChange(false)
      })
  }

  return { saving, error, setError, run }
}

/** 정책 1건 저장(택배사 · 출고번호 접두 카드). */
export const usePolicySave = (
  warehouseCode: string,
  callbacks: WriteCallbacks,
): { saving: boolean; error: PanelError | null; save: (policy: WarehousePolicy) => void } => {
  const { saving, error, run } = useWriteAction(callbacks)

  const save = (policy: WarehousePolicy): void => run(() => saveWarehousePolicy(warehouseCode, policy), callbacks.onSaved)

  return { saving, error, save }
}
